import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { FieldError } from 'react-hook-form';
import { TestContext } from 'yup';

import { Status } from '@/models/form/form';

import { isDefined } from '../guards';

export const removeTrailingZeros = (value: string) => {
  const cleanedNumber = value.toString().replace(/,/g, '');
  let formattedValue = Number(value);

  if (value) {
    const [integralDigits, fractionalDigits] = cleanedNumber.split('.');
    if (
      (fractionalDigits &&
        fractionalDigits?.length === 1 &&
        fractionalDigits[0] === '0') ||
      (fractionalDigits?.length === 2 &&
        fractionalDigits[0] === '0' &&
        fractionalDigits[1] === '0')
    ) {
      formattedValue = Number(integralDigits);
    } else if (
      fractionalDigits &&
      fractionalDigits?.length === 2 &&
      fractionalDigits[1] === '0'
    ) {
      formattedValue = Number(
        cleanedNumber.substring(0, cleanedNumber.length - 1)
      );
    }
  }
  return formattedValue;
};

export const isValid = (date: Date) => {
  return dayjs(date, 'MM/DD/YYYY', true).isValid();
};

export const handleStatus = (
  isFocused: boolean,
  error: FieldError | undefined,
  isTouched: boolean,
  value: any
): Status => {
  if (isFocused && !error) {
    return Status.FOCUSED;
  }
  if (
    !isFocused &&
    !error &&
    isTouched &&
    value &&
    !isEmpty(value.toString())
  ) {
    return Status.SUCCESS;
  }
  if (isFocused && error && !value) {
    return Status.FOCUSED;
  }
  if (isFocused && error) {
    return Status.ERROR;
  }
  if (!isFocused && error && isTouched) {
    return Status.ERROR;
  }

  return Status.NONE;
};

export const PHONE_REGULAR_EXPRESSION =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export function emptyStringToNull(value: any, originalValue: any) {
  if (typeof originalValue === 'string' && originalValue === '') {
    return null;
  }

  return value;
}

export function stringToNumber(value: string | number) {
  if (!value) return null;

  if (typeof value === 'number') return value;

  const newStr = value.replace(/,/g, '');

  return Number(newStr);
}

export function getMinMaxTest(minKey: string, maxKey: string) {
  return {
    message: 'Min cannot exceed max',
    name: minKey,
    test(value) {
      const parentKey = stringToNumber(this.parent[maxKey]);
      const newValue = stringToNumber(value);

      if (parentKey === null || Number.isNaN(parentKey)) {
        return true;
      }

      return parentKey >= (newValue || 0);
    },
  };
}

export const minAndMaxNumberTest = (
  value: {
    min?: string | number | null | undefined;
    max?: string | number | null | undefined;
  } | null,
  ctx: TestContext,
  options?: {
    allowNegative: boolean;
  }
) => {
  if (!value) return true;
  let { min, max } = value;
  if (!isDefined(min) && !isDefined(max)) return true;

  // If present, cast to numbers
  min = isDefined(min) ? Number(min) : min;
  max = isDefined(max) ? Number(max) : max;

  if (min && Number.isNaN(min)) {
    return ctx.createError({ message: 'The limits must be numbers.' });
  }
  if (max && Number.isNaN(max)) {
    return ctx.createError({ message: 'The limits must be numbers.' });
  }

  if (typeof min === 'number' && min < 0 && !options?.allowNegative) {
    return ctx.createError({
      message: 'The lower limit must be a positive number.',
    });
  }
  if (isDefined(min) && isDefined(max) && min > max) {
    return ctx.createError({
      message: 'The lower limit must be below the upper limit',
    });
  }
  return true;
};

export const minAndMaxDateTest = (
  value: {
    min?: Date | null | undefined;
    max?: Date | null | undefined;
  } | null,
  ctx: TestContext
) => {
  if (!value) return true;
  const { min, max } = value;
  if (!min && !max) return true;
  if (min && max && min > max) {
    return ctx.createError({ message: 'Min date must be before max date' });
  }
  return true;
};
